import type { ReactNode } from 'react'
import type { Role } from '@accounting/shared'
import { ROLE_LABELS } from '@accounting/shared'
import { cx } from './cx'

export type Tone = 'brand' | 'accent' | 'neutral' | 'positive' | 'warning' | 'critical'

const TONES: Record<Tone, string> = {
  brand: 'bg-brand-50 text-brand-800 ring-brand-200',
  accent: 'bg-accent-50 text-accent-700 ring-accent-200',
  neutral: 'bg-ink-100 text-ink-700 ring-ink-200',
  positive: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  critical: 'bg-red-50 text-red-800 ring-red-200',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap',
        TONES[tone],
        className
      )}
    >
      {children}
    </span>
  )
}

/**
 * One colour per role, used everywhere a role is named.
 *
 * Consistency matters more than the particular assignment: someone scanning the
 * user list should be able to spot every Finance account by colour alone, which
 * only works if the colour never varies by screen.
 */
const ROLE_TONES: Record<Role, Tone> = {
  finance: 'brand',
  legal_production: 'accent',
  executive: 'neutral',
}

export function RoleBadge({ role }: { role: Role }) {
  return <Badge tone={ROLE_TONES[role]}>{ROLE_LABELS[role]}</Badge>
}
